import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DocumentsImportService } from 'wflow-main-app/src/app/shared/services/documents/documents-import.service';
import {
  RegisterFactoryList,
  RegisterFactoryService,
} from '../../services/registers/register-factory.service';
import { ImportFileDialogComponent } from './import-file-dialog.component';
import {
  ImportFileTrackingHooks,
  ImportSubmitData,
} from './import-file-dialog.interface';
import { WfDialogService } from 'wflow-ui';

export interface RegisterImportSubmitData {
  finished: boolean;
  registerCode: RegisterFactoryList;
  success: boolean;
  data: any;
}

@Injectable()
export class ImportFileDialogService {
  /**
   * Failed document-import submits per file name, kept across dialog openings —
   * a failed import can only be retried by opening the dialog again, so this has
   * to outlive the dialog for `retryAfterFailure` to ever be true. Lives as long
   * as the surface that provides this service (the grid page).
   */
  readonly #documentImportFailedAttempts = new Map<string, number>();

  constructor(
    private _translate: TranslateService,
    private dialogSvc: WfDialogService,
    private _registersFactoryService: RegisterFactoryService,
    private _documentsImportService: DocumentsImportService,
  ) {}

  public importFileRegister<T = any>(
    code: RegisterFactoryList,
    title: string,
    response$?: Subject<T>,
  ): Observable<any> {
    const afterCloseData = signal<ImportSubmitData>(null);

    return this.dialogSvc
      .open(ImportFileDialogComponent, {
        title: this._translate.instant(title),
        width: '400px',
        data: {
          importService: this._registersFactoryService.get(code),
          response$,
          afterCloseData: afterCloseData,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(() => afterCloseData()?.finished),
        map(() => afterCloseData()),
      );
  }

  /**
   * @param tracking optional per-submit hooks so the caller can track the import
   *   from its own surface (this dialog is shared with the register imports).
   */
  public importFileInternalCode(
    hasInfoBlock: boolean = false,
    tracking?: ImportFileTrackingHooks,
  ): Observable<any> {
    const afterCloseData = signal<ImportSubmitData>(null);

    return this.dialogSvc
      .open(ImportFileDialogComponent, {
        title: this._translate.instant('DOCUMENT.PAPER.InternalCode'),
        width: '400px',
        data: {
          hasInfoBlock: hasInfoBlock,
          importService: this._documentsImportService,
          afterCloseData: afterCloseData,
          tracking,
          failedAttemptsStore: this.#documentImportFailedAttempts,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(() => afterCloseData()?.finished),
      );
  }
}
